"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Clock, AlertTriangle, Star, Users, Zap, Shield, X } from "lucide-react";
import { HOTMART_CONFIG, PRICING, UPSELL_VIDEOS } from "@/config/constants";
import { Footer } from "@/components/Footer";
import { VturbPlayer } from "@/components/VturbPlayer";

export const Upsell5Mentorias = () => {
  const pricing = PRICING.upsell5Mentorias;
  const checkoutUrl = HOTMART_CONFIG.upsell5Mentorias;

  useEffect(() => {
    // Carregar script do Hotmart Sales Funnel
    const script = document.createElement('script');
    script.src = 'https://checkout.hotmart.com/lib/hotmart-checkout-elements.js';
    script.async = true;
    script.onload = () => {
      // Inicializar o widget após o script carregar
      if ((window as any).checkoutElements) {
        (window as any).checkoutElements.init('salesFunnel').mount('#hotmart-sales-funnel');
      }
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remover script ao desmontar componente
      document.body.removeChild(script);
    };
  }, []);

  const handleAccept = () => {
    // Track conversion
    if ((window as any).fbq) {
      (window as any).fbq("track", "InitiateCheckout", {
        content_name: "Upsell 5 Mentorias",
        currency: "BRL",
        value: 997,
      });
    }
    window.location.href = checkoutUrl;
  };

  const handleDecline = () => {
    // Redirecionar para página de obrigado ou próximo passo
    window.location.href = "/obrigado";
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header com urgência */}
      <div className="bg-red-600 py-4 md:py-5 px-4 text-center">
        <div className="flex items-center justify-center gap-2 text-white font-bold text-base md:text-lg">
          <Clock className="h-5 w-5 md:h-6 md:w-6 animate-pulse" />
          <span>ATENÇÃO: ESTA É UMA OFERTA ÚNICA DESTA PÁGINA</span>
        </div>
      </div>

      <section className="relative py-12 md:py-20 overflow-hidden">
        {/* Background effects */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-primary">
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/15 rounded-full blur-[100px]" />
        </div>

        <div className="container px-4 relative z-10">
          <div className="max-w-4xl mx-auto">

            {/* Headline */}
            <h1 className="font-black text-center leading-tight mb-6">
              <span className="block text-2xl md:text-3xl lg:text-4xl mb-2">
                ANTES DE CONTINUAR,<br />
                <span className="bg-gradient-accent bg-clip-text text-transparent">
                  QUERO TE ENTREGAR UM CAMINHO MAIS PERSONALIZADO!
                </span>
              </span>
              <span className="block text-3xl md:text-5xl lg:text-6xl">
                em uma OPORTUNIDADE ESPECIAL desta página!
              </span>
            </h1>

            {/* Vídeo de apresentação */}
            <div className="mb-10 flex justify-center">
              <div className="relative w-full max-w-sm rounded-2xl sm:rounded-3xl p-1 sm:p-2 bg-gradient-to-br from-primary/30 via-card/20 to-accent/30">
                <VturbPlayer
                  videoId={UPSELL_VIDEOS.upsell5Mentorias.id}
                  scriptUrl={UPSELL_VIDEOS.upsell5Mentorias.url}
                  className="relative aspect-[9/16] w-full rounded-xl sm:rounded-2xl overflow-hidden"
                />
              </div>
            </div>

             {/* Botão CTA abaixo do vídeo */}
            <div className="mb-10 flex justify-center">
              {/* <Button
                onClick={handleAccept}
                className="w-full max-w-sm px-8 md:px-10 py-8 md:py-8 text-base md:text-xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-300 hover:to-emerald-400 shadow-lg shadow-green-500/50 hover:shadow-green-400/70 transition-all hover:-translate-y-1"
              >
                🔓 GARANTIR AGORA
              </Button> */}
              {/* HOTMART - Sales Funnel Widget */}
              <div id="hotmart-sales-funnel" className="w-full max-w-4xl mx-auto mt-10"></div>
            </div>

            {/* Card principal */}
            <Card className="relative border-2 border-primary/60 shadow-[0_0_60px_rgba(100,192,242,0.3)] overflow-hidden mb-10">
              {/* Glow interno */}
              <div
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                  background: "radial-gradient(800px 300px at 50% 0%, hsl(var(--primary)/0.15), transparent 70%)",
                }}
              />

              <CardContent className="relative p-6 md:p-10">
                {/* Título do produto */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">
                    🎯 Pacote de 5 Mentorias Individuais
                  </h2>
                  <p className="text-muted-foreground">
                    Acompanhamento direto e personalizado com quem já passou pelo que você está vivendo
                  </p>
                </div>

                {/* Benefícios */}
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">5 sessões individuais de 1 hora</p>
                      <p className="text-sm text-muted-foreground">Foco total nas suas dúvidas e dificuldades específicas</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Plano de ação personalizado</p>
                      <p className="text-sm text-muted-foreground">Estratégias sob medida para sua situação</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Suporte via WhatsApp entre sessões</p>
                      <p className="text-sm text-muted-foreground">Não fique sozinho nos momentos difíceis</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Acompanhamento de progresso</p>
                      <p className="text-sm text-muted-foreground">Métricas e ajustes para garantir resultados</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Técnicas avançadas de prevenção</p>
                      <p className="text-sm text-muted-foreground">Ferramentas exclusivas para evitar recaídas</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Acesso prioritário vitalício</p>
                      <p className="text-sm text-muted-foreground">Canal direto para dúvidas futuras</p>
                    </div>
                  </div>
                </div>

                {/* Comparação */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Sem mentoria */}
                  <div className="bg-red-950/30 border border-red-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <X className="h-5 w-5 text-red-400" />
                      <h3 className="font-bold text-red-400">Sem acompanhamento</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>❌ Caminha sozinho, sem direção clara</li>
                      <li>❌ Demora meses ou anos para ver resultados</li>
                      <li>❌ Alto risco de recaídas sem suporte</li>
                      <li>❌ Dúvidas ficam sem resposta</li>
                      <li>❌ Perde tempo com tentativa e erro</li>
                    </ul>
                  </div>

                  {/* Com mentoria */}
                  <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                      <h3 className="font-bold text-emerald-400">Com as 5 mentorias</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>✅ Acompanhamento direto e personalizado</li>
                      <li>✅ Resultados acelerados em semanas</li>
                      <li>✅ Suporte nos momentos críticos</li>
                      <li>✅ Todas as dúvidas respondidas</li>
                      <li>✅ Caminho certeiro sem desperdício</li>
                    </ul>
                  </div>
                </div>

                {/* Preço */}
                <div className="text-center mb-8">
                  <div className="flex flex-col items-center gap-2 mb-4">
                    <span className="text-lg text-muted-foreground">
                      De <span className="line-through text-red-400">{pricing.priceOriginal}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-4 py-1 text-sm font-bold uppercase tracking-wide text-yellow-400">
                      Oferta exclusiva desta página
                    </span>
                  </div>
                  <div className="text-lg text-muted-foreground mb-1">Por apenas</div>
                  <div className="text-5xl md:text-7xl font-black text-white mb-2">
                    {pricing.price}
                  </div>
                  <div className="text-muted-foreground">
                    ou {pricing.priceInstallment} em {pricing.installments} no cartão
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col items-center gap-4">
                  {/* <Button
                    onClick={handleAccept}
                    className="w-full max-w-lg px-4 md:px-8 py-6 text-xs md:text-xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-300 hover:to-emerald-400 shadow-lg shadow-green-500/50 hover:shadow-green-400/70 transition-all hover:-translate-y-1"
                  >
                    🚀 QUERO ACELERAR MINHA TRANSFORMAÇÃO!
                  </Button> */}
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="h-4 w-4 text-emerald-400" />
                    <span>Pagamento 100% seguro • Garantia de 7 dias</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Seção "E se eu não pegar isso agora?" */}
            <div className="bg-gradient-to-br from-red-950/40 to-orange-950/30 border border-red-500/30 rounded-2xl p-6 md:p-8 mb-10">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-8 w-8 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-yellow-400">
                    E se eu não aproveitar essa oportunidade agora?
                  </h3>
                  <div className="space-y-3 text-muted-foreground">
                    <p>
                      Entenda: esta oferta <strong className="text-foreground">existe apenas nesta página</strong>. 
                      Se você fechar ou voltar depois, esse preço e essas condições não estarão mais disponíveis.
                    </p>
                    <p>
                      Você pode continuar sozinho, claro. Muita gente faz isso. 
                      Mas a verdade é que <strong className="text-foreground">sem acompanhamento, a maioria demora muito mais</strong> para 
                      ver resultados — e muitos acabam desistindo no meio do caminho.
                    </p>
                    <p>
                      Essa é sua chance de ter alguém que já trilhou esse caminho 
                      <strong className="text-foreground"> ao seu lado, te guiando passo a passo</strong>, 
                      tirando dúvidas específicas e te ajudando a superar os momentos mais difíceis.
                    </p>
                    <p className="text-foreground font-semibold">
                      A decisão é sua. Mas pense bem antes de deixar essa oportunidade passar.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Upsell5Mentorias;
